import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { SparklesIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const AssistantChat = lazy(() => import('./AssistantChat'));

/** 拖拽头高度，用于判断鼠标是否在可拖拽区域 */
const DRAG_HANDLE_HEIGHT = 46;

/** 限制数值在 min-max 范围 */
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * 全局 AI 助手入口。
 * 负责悬浮按钮、弹窗显示和拖拽位置管理，聊天业务由 AssistantChat 承载。
 */
export function GlobalAIAssistant() {
  const [open, setOpen] = useState(false);
  /** 首次打开后保持聊天面板挂载，避免关闭悬浮窗时丢失会话、草稿和生成状态 */
  const [hasOpened, setHasOpened] = useState(false);

  /** 首次打开时再加载聊天模块，后续关闭只隐藏弹窗而不销毁聊天实例 */
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setHasOpened(true);
  };

  /** 弹窗当前偏移量 */
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  /** 触发按钮位置 */
  const [btnPosition, setBtnPosition] = useState(() => {
    const saved = localStorage.getItem('ai-assistant-btn-position');
    if (saved) {
      try { return JSON.parse(saved); } catch { /* ignore */ }
    }
    return { right: 24, bottom: 24 };
  });
  const [btnDragging, setBtnDragging] = useState(false);
  const [ignoreNextBtnClick, setIgnoreNextBtnClick] = useState(false);
  const btnDragStartRef = useRef({ pointerX: 0, pointerY: 0, right: 24, bottom: 24 });
  const btnRef = useRef<HTMLButtonElement>(null);

  /** 是否处于拖拽状态 */
  const [dragging, setDragging] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  /** Portal 中的面板节点就绪后再绑定滚轮监听 */
  const setContentRef = useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    setContentElement(node);
  }, []);

  /**
   * 隔离面板内的纵向滚动
   * React 的 wheel 合成事件使用 passive 监听，改用原生非 passive 监听确保滚动边界处可以阻止背景页面滚动
   */
  useEffect(() => {
    const panel = contentElement;
    if (!panel) return undefined;

    /**
     * 面板内存在可继续滚动的容器时保留默认行为，到达滚动边界或当前区域不可滚动时阻止滚轮带动背景页面
     */
    const handleWheel = (event: WheelEvent) => {
      if (!event.deltaY) return;

      const scrollDown = event.deltaY > 0;
      let element = event.target instanceof Element ? event.target : null;

      while (element && panel.contains(element)) {
        if (element instanceof HTMLElement) {
          const { overflowY, flexDirection } = window.getComputedStyle(element);
          const isScrollable =
            /(auto|scroll|overlay)/.test(overflowY) &&
            element.scrollHeight > element.clientHeight + 1;

          if (isScrollable) {
            // Bubble.List 的倒序滚动容器以 0 为底部，上滑时 scrollTop 为负值
            const canScroll = flexDirection === 'column-reverse'
              ? scrollDown
                ? element.scrollTop < -1
                : element.scrollTop > element.clientHeight - element.scrollHeight + 1
              : scrollDown
                ? element.scrollTop + element.clientHeight < element.scrollHeight - 1
                : element.scrollTop > 1;

            if (canScroll) return;
          }
        }

        if (element === panel) break;
        element = element.parentElement;
      }

      event.preventDefault();
    };

    panel.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      panel.removeEventListener('wheel', handleWheel);
    };
  }, [contentElement]);

  /** 拖拽起始信息 */
  const dragStartRef = useRef({
    pointerX: 0,
    pointerY: 0,
    offsetX: 0,
    offsetY: 0,
    minX: 0,
    maxX: 0,
    minY: 0,
    maxY: 0,
  });

  /** 监听全局 pointermove / pointerup */
  useEffect(() => {
    if (!dragging) return;

    const originalUserSelect = document.body.style.userSelect;

    document.body.style.userSelect = 'none';

    const handlePointerMove = (event: PointerEvent) => {
      const { pointerX, pointerY, offsetX, offsetY, minX, maxX, minY, maxY } = dragStartRef.current;

      /** 根据鼠标移动计算新的偏移，并限制边界 */
      setDragOffset({
        x: clamp(offsetX + event.clientX - pointerX, minX, maxX),
        y: clamp(offsetY + event.clientY - pointerY, minY, maxY),
      });
    };

    const handlePointerUp = () => {
      setDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      document.body.style.userSelect = originalUserSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [dragging]);

  /** 按钮拖拽逻辑 */
  useEffect(() => {
    if (!btnDragging) return;

    const originalUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const handlePointerMove = (event: PointerEvent) => {
      const { pointerX, pointerY, right, bottom } = btnDragStartRef.current;
      const deltaX = event.clientX - pointerX;
      const deltaY = event.clientY - pointerY;
      const hasDragged = Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5;

      // 超过 5px 移动才算拖拽，区分点击
      if (hasDragged) {
        const newRight = clamp(right - deltaX, 0, window.innerWidth - 48);
        const newBottom = clamp(bottom - deltaY, 0, window.innerHeight - 48);
        setBtnPosition({ right: newRight, bottom: newBottom });
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      setBtnDragging(false);
      const { pointerX, pointerY, right, bottom } = btnDragStartRef.current;
      const deltaX = event.clientX - pointerX;
      const deltaY = event.clientY - pointerY;
      const hasDragged = Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5;

      setIgnoreNextBtnClick(hasDragged);

      if (hasDragged) {
        // 吸附到最近的屏幕边缘（左或右）
        const currentRight = clamp(right - deltaX, 0, window.innerWidth - 48);
        const currentBottom = clamp(bottom - deltaY, 0, window.innerHeight - 48);
        const currentLeft = window.innerWidth - currentRight - 48;

        // 判断离左边还是右边更近
        const snapRight = currentLeft > window.innerWidth / 2 ? 16 : window.innerWidth - 48 - 16;
        const finalPosition = { right: snapRight, bottom: clamp(currentBottom, 16, window.innerHeight - 64) };

        setBtnPosition(finalPosition);
        localStorage.setItem('ai-assistant-btn-position', JSON.stringify(finalPosition));
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      document.body.style.userSelect = originalUserSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [btnDragging]);

  /** 按钮拖拽开始 */
  const handleBtnPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    setIgnoreNextBtnClick(false);
    btnDragStartRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      right: btnPosition.right,
      bottom: btnPosition.bottom,
    };
    setBtnDragging(true);
  };

  /** 阻止拖拽后触发点击 */
  const handleBtnClick = (event: React.MouseEvent) => {
    if (ignoreNextBtnClick) {
      event.preventDefault();
      event.stopPropagation();
      setIgnoreNextBtnClick(false);
    }
  };

  const handleDragStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;

    const content = contentRef.current;
    if (!content) return;

    const rect = content.getBoundingClientRect();
    const isInDragHandle = event.clientY - rect.top <= DRAG_HANDLE_HEIGHT;

    if (!isInDragHandle) return;

    const target = event.target as HTMLElement;
    const interactiveElement = target.closest(
      'button, input, textarea, select, a, [role="button"], [role="textbox"], [data-radix-collection-item]'
    );

    if (interactiveElement) return;

    dragStartRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      offsetX: dragOffset.x,
      offsetY: dragOffset.y,
      minX: dragOffset.x - rect.left,
      maxX: dragOffset.x + window.innerWidth - rect.right,
      minY: dragOffset.y - rect.top,
      maxY: dragOffset.y + window.innerHeight - rect.bottom,
    };
    setDragging(true);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              ref={btnRef}
              type="button"
              data-ai-assistant-trigger="true"
              aria-label="智能助手"
              onPointerDown={handleBtnPointerDown}
              onClick={handleBtnClick}
              className={cn(
                'fixed z-50 flex size-12 items-center justify-center',
                'gap-0 rounded-full border-0 p-0 text-white',
                'bg-primary shadow-[0_12px_28px_rgba(0,116,255,0.28)]',
                'hover:brightness-105 active:scale-95 active:brightness-95',
                '[&>svg]:m-0 [&>svg]:size-5 [&>svg]:shrink-0',
                btnDragging ? 'cursor-grabbing' : 'transition-all duration-300 cursor-grab'
              )}
              style={{
                right: `${btnPosition.right}px`,
                bottom: `${btnPosition.bottom}px`,
              }}
            >
              <SparklesIcon className="text-white" data-icon="inline-start" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="left" sideOffset={8}>
          智能助手
        </TooltipContent>
      </Tooltip>

      {/** 面板以视口右下角为锚点，并给右侧悬浮按钮留出空间 */}
      <PopoverAnchor
        style={{ position: 'fixed', right: 88, bottom: 16, width: 1, height: 1, pointerEvents: 'none' }}
      />

      <PopoverContent
        ref={setContentRef}
        forceMount
        side="top"
        align="end"
        sideOffset={0}
        onPointerDown={handleDragStart}
        onInteractOutside={(event) => {
          const target = event.target as HTMLElement;

          if (target.closest('[data-ai-assistant-trigger="true"]')) return;
          if (target.closest('[data-ai-assistant-model-select-content="true"]')) return;
          if (
            target
              .closest('[data-radix-popper-content-wrapper]')
              ?.querySelector('[data-ai-assistant-model-select-content="true"]')
          ) {
            return;
          }

          event.preventDefault();
        }}
        className={cn(
          'z-[50] overflow-hidden p-0',
          'rounded-xl border border-border bg-background p-0 shadow-xl',
          'data-[state=closed]:hidden'
        )}
        style={{
          width: 'min(1200px, max(420px, calc(100vw - 32px)))',
          height: 'min(720px, calc(100vh - 100px))',
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
        }}
      >
        <div className="flex h-full min-h-0 flex-col">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="关闭 AI 助手"
            className="absolute top-2 right-2 z-10 bg-background/80 backdrop-blur"
            onClick={() => setOpen(false)}
          >
            <XIcon data-icon="inline-start" />
          </Button>
          <div className="min-h-0 flex-1">
            {hasOpened ? (
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    <Spinner className="size-5" />
                  </div>
                }
              >
                <AssistantChat />
              </Suspense>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default GlobalAIAssistant;
